import { useCallback, useContext, useEffect, useState } from 'react'
import { OptionsContext } from '../Context/OptionsContext'
import {
  EnergyOptionInterface,
  InsulationDataInterface,
  InsulationResponseInterface, PersonalInfoInterface,
  ProxyAddressInterface, ProxyResultsInterface
} from '../interfaces'
import { energyOptions as staticEnergyOptions } from '../static'
import { StepperContext } from '../Context/StepperContext'
import { MainFrameContext } from '../../UI/components/MainFrame'
import { SharedDataContext } from '../Context/SharedDataContext'
import { apiEnv } from '../../index'
import TagManager from 'react-gtm-module'
import Cookies from 'js-cookie'
import { applyInsulationMeasures, dataLayerPush, extractAdditionFromHouseNumber } from '../helperFunctions'
import { getRequest, postRequest, putRequest } from '../Api'
import { AxiosError } from 'axios'

const useAppController = () => {
  
  const queryParams = new URLSearchParams(window.location.search)
  const initialPostcode = queryParams.get('postcode')
  const initialAddress = queryParams.get('housenumber')
  
  const options = useContext(OptionsContext)
  
  const [inputAddress, setInputAddress] = useState<{ address: string, postCode: string }>({
    address: initialAddress || '',
    postCode: initialPostcode || ''
  })
  const [addressString, setAddressString] = useState('')
  const [coordinates, setCoordinates] = useState({ lat: -1, lng: -1 })
  const [energyOptions, setEnergyOptions] = useState<EnergyOptionInterface[]>(staticEnergyOptions)
  const [pending, setPending] = useState(false)
  const [insulationData, setInsulationData] = useState<InsulationDataInterface>()
  const [leadsource, setLeadsource] = useState(String(options.leadsource))
  
  const [proxyAddressRes, setProxyAddressRes] = useState<ProxyAddressInterface>()
  const [consumption, setConsumption] = useState(0)
  
  const { dispatchStep, flow, isUpdateRequest, setIsUpdateRequest } = useContext(StepperContext)
  const { triggerErrorPopup } = useContext(MainFrameContext)
  
  const { setPersonalInfo } = useContext(SharedDataContext)
  
  useEffect(() => {
    const gtmId = apiEnv === 'LIVE' ? process.env.REACT_APP_GTM_ID_PRODUCTION : process.env.REACT_APP_GTM_ID_STAGING
    const tagManagerArgs = {
      gtmId: gtmId || ''
    }
    TagManager.initialize(tagManagerArgs)
    checkAndRedirectToStep6() //eslint-disable-next-line
  }, [])
  
  useEffect(() => {
    if (flow === 'insulation') {
      setEnergyOptions(options =>
        options.filter(option => option.type === 'energyLossPrevention')
      )
    } else if (flow === 'completeAdvice') setEnergyOptions(staticEnergyOptions)
  }, [flow])
  
  useEffect(() => {
    const gfMedium = Cookies.get('gf_medium_parameter')
    options.leadsource === 179 && gfMedium && setLeadsource(gfMedium)
  }, [options.leadsource])
  
  const checkAndRedirectToStep6 = () => {
    const firstName = queryParams.get('firstname')
    const lastName = queryParams.get('lastname')
    const email = queryParams.get('email')
    const zip = queryParams.get('zip')
    const houseNumber = queryParams.get('housenumber')
    if (firstName && lastName && email && zip && houseNumber) {
      setPersonalInfo({ firstName, lastName, email })
      setInputAddress({
        address: houseNumber,
        postCode: zip
      })
      dispatchStep('redirect_personal_info')
    }
  }
  
  const setLocationData = useCallback((address: string, postCode: string) => {
    setPending(true)
    setInputAddress({ address, postCode })
    const [houseNumber, addition] = extractAdditionFromHouseNumber(address)
    
    getRequest('/housescan/insulation/address', {
        postcode: postCode,
        number: houseNumber,
        addition
      }, (data: InsulationResponseInterface) => {
        const { lat, lon, address, ...insulationData } = data
        setInsulationData(insulationData)
        setCoordinates({
          lat,
          lng: lon
        })
        setAddressString(address)
        dispatchStep('next')
        setPending(false)
      }, (err: AxiosError) => {
        if (err.response?.status === 404) {
          setAddressString(`${postCode}, ${address}`)
          dispatchStep('addressError')
        } else triggerErrorPopup()
        setPending(false)
      }
    )
  }, [dispatchStep, triggerErrorPopup])
  
  useEffect(() => {
    if (initialPostcode && initialAddress) {
      setLocationData(initialAddress, initialPostcode.toUpperCase())
    }
  }, [initialAddress, initialPostcode, setLocationData])
  
  const setProxyAddress = () => {
    setPending(true)
    const params = {
      postcode: inputAddress.postCode,
      number: inputAddress.address,
      leadsource
    }
    getRequest('/housescan/roof/address', params, (response: ProxyAddressInterface) => {
      setProxyAddressRes(response)
      dispatchStep('next')
      setPending(false)
    }, () => {
      setPending(false)
      triggerErrorPopup()
    })
  }
  
  const getSavings = (options: EnergyOptionInterface[]) => {
    
    const params = {
      sqm_roofs: insulationData?.sqm_roofs,
      sqm_ground: insulationData?.sqm_ground,
      sqm_walls: insulationData?.sqm_walls,
      kwh: consumption,
      sqm_glass: insulationData?.sqm_glass
    }
    
    interface SavingInterface {
      savings: string,
      costs: string,
      subsidy: string,
      co2Savings: string
    }
    
    interface SavingsResponseInterface {
      roof: SavingInterface,
      wall: SavingInterface,
      ground: SavingInterface,
      glass: SavingInterface,
      solar: SavingInterface
    }
    
    getRequest('/housescan/savings', params, (data: SavingsResponseInterface) => {
      //  TODO: there has to be a better solution for mapping this
      const optionsBuffer = options.map(option => {
        if (option.id && Object.keys(data).includes(option.id)) {
          const savingsObj = Object.entries(data).filter(([key]) => key === option.id)[0][1]
          
          return {
            ...option,
            ...savingsObj
          }
        } else return option
      })
      setEnergyOptions(optionsBuffer)
      dispatchStep('next')
      setPending(false)
    }, () => {
      triggerErrorPopup()
    })
  }
  
  const getProxyResults = (repetition: number) => {
    if (repetition < 50) {
      setPending(true)
      const params = {
        transaction: proxyAddressRes?.transaction,
        wait: true,
        leadsource
      }
      getRequest('/housescan/roof/results', params, (response: ProxyResultsInterface) => {
        if (response.status === 'pending') setTimeout(() => getProxyResults(repetition + 1), 500)
        else if (response.status === 'ready') {
          const optionsBuffer = energyOptions.map(option =>
            option.id === 'solar' ?
              {
                ...option,
                backgroundImage: 'https://api.solease.nl' + response.background_map_url,
                panelsImage: 'https://api.solease.nl' + response.imgsrc,
                panelsNumber: response.modules
              } : option
          )
          response.modules < 4 ? console.log('todocase: less than 4 panels') : console.log('panels ok')
          getSavings(optionsBuffer)
        }
      }, () => {
        setPending(false)
        triggerErrorPopup()
      })
    } else {
      //  T setStep(9)
    }
  }
  
  const setProxyConsumption = () => {
    if (consumption) {
      const params = `?e-consumption=${consumption}&transaction=${proxyAddressRes?.transaction}`
      putRequest('/housescan/roof/consumption' + params, {}, () => {
        getProxyResults(1)
      }, () => {
        triggerErrorPopup()
      })
    }
  }
  
  const step3Submit = (data: { sqm_ground: number, sqm_roofs: number, sqm_walls: number, sqm_glass: number, construction_year: number }) => {
    setEnergyOptions(applyInsulationMeasures(energyOptions, data.construction_year))
    setProxyAddress()
    if (insulationData) {
      const insulationDataBuffer: InsulationDataInterface = { ...insulationData }
      setInsulationData({ ...insulationDataBuffer, ...data })
    }
  }
  
  const step4Submit = (options: EnergyOptionInterface[], consumptionParam: number) => {
    setConsumption(consumptionParam)
    setEnergyOptions(options)
    if (consumptionParam === consumption) dispatchStep('next')
  }
  
  useEffect(() => {
    consumption && setProxyConsumption() //eslint-disable-next-line
  }, [consumption])
  
  const submitHouseScanApplication = (personalInfo: PersonalInfoInterface, options: EnergyOptionInterface[], contractType: string) => {
    setPending(true)
    const solar = options.filter(option => option.id === 'solar')[0]
    const insulationOptions: { [key: string]: any } = {}
    console.log(options)
    options.forEach(option => {
      if (option.type === 'energyLossPrevention') {
        insulationOptions[`${option.id}`] = option.selected ? {
          sqm: insulationData ? insulationData[`sqm_${option.id}` as keyof typeof insulationData] : 0,
          savings: option.savings,
          co2Savings: option.co2Savings,
          costs: option.costs,
          subsidy: option.subsidy
        } : false
      }
    })
    
    console.log(insulationOptions)
    
    const updateBody = {
      firstname: personalInfo.firstName,
      lastname: personalInfo.lastName,
      email: personalInfo.email,
      phone: personalInfo.phone,
      contractType: solar?.selected ? contractType : false,
      postcode: inputAddress.postCode,
      number: inputAddress.address,
      leadsource,
      solar: solar?.selected,
      insulation: {
        ground: !!insulationOptions.ground,
        wall: !!insulationOptions.wall,
        roof: !!insulationOptions.roof,
        glass: !!insulationOptions.glass
      }
    }
    
    const submitBody = {
      firstname: personalInfo.firstName,
      lastname: personalInfo.lastName,
      email: personalInfo.email,
      phone: personalInfo.phone,
      postcode: inputAddress.postCode,
      number: inputAddress.address,
      contractType: solar?.selected ? contractType : false,
      leadsource,
      consumption,
      solar: solar?.selected ? {
        moduleUrl: solar.panelsImage?.split('/housescan')[1],
        backgroundUrl: solar.backgroundImage?.split('/housescan')[1],
        moduleCount: solar.panelsNumber,
        savings: solar.savings,
        co2Savings: solar.co2Savings,
        paybackPeriod: '4',
        kwh: consumption,
        transaction: proxyAddressRes?.transaction
      } : false,
      insulation: insulationOptions
    }
    
    postRequest(isUpdateRequest ? '/housescan/submit/update' : '/housescan/submit/', isUpdateRequest ? updateBody : submitBody, () => {
      dispatchStep('next')
      setPending(false)
    }, () => {
      setPending(false)
      triggerErrorPopup()
    })
  }
  
  const downloadPdf = (personalInfo: PersonalInfoInterface, options: EnergyOptionInterface[]) => {
    const solar = options.filter(option => option.id === 'solar')[0]
    const insulationOptions: { [key: string]: any } = {}
    options.forEach(option => {
      if (option.type === 'energyLossPrevention') {
        insulationOptions[`${option.id}`] = {
          savings: option.savings,
          co2Savings: option.co2Savings,
          costs: option.costs,
          subsidy: option.subsidy
        }
      }
    })
    
    const body = {
      firstname: personalInfo.firstName,
      lastname: personalInfo.lastName,
      email: personalInfo.email,
      postcode: inputAddress.postCode,
      number: inputAddress.address,
      solar: solar ? {
        moduleUrl: solar.panelsImage?.split('/housescan')[1],
        backgroundUrl: solar.backgroundImage?.split('/housescan')[1],
        moduleCount: solar.panelsNumber,
        savings: solar.savings,
        co2Savings: solar.co2Savings,
        paybackPeriod: 4,
        kwh: consumption
      } : false,
      insulation: insulationOptions
    }
    
    postRequest('/housescan/submit/download', body, () => {
      setIsUpdateRequest(true)
      dataLayerPush('Download Advice', 'sent')
    }, () => {
      triggerErrorPopup()
    })
  }
  
  return {
    pending,
    inputAddress,
    setLocationData,
    coordinates,
    insulationData,
    step3Submit,
    energyOptions,
    setEnergyOptions,
    step4Submit,
    downloadPdf,
    submitHouseScanApplication,
    addressString
  }
}

export default useAppController
